import { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";

const INITIALS = (name = "") =>
  name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "BA";

export default function LeaderboardPage() {
  const { lang } = useLang();
  const t = useDict(lang);
  const [scope, setScope] = useState("month");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.leaderboard(scope, 10).then((r) => setRows(r.rows || [])).catch(() => {});
  }, [scope]);

  return (
    <div data-testid="leaderboard-page" className="max-w-[1100px] mx-auto px-6 lg:px-12 pt-16 pb-24">
      <p className="eyebrow mb-3 flex items-center gap-3">
        <Trophy size={14} color="var(--red)" strokeWidth={1.5} />
        {t.leaderboard.eyebrow}
      </p>
      <h1 className="font-serif-display text-5xl sm:text-6xl leading-[1.02] mb-4">
        {t.leaderboard.pageTitle}
      </h1>
      <p className="text-base text-[color:var(--ink-2)] max-w-2xl leading-relaxed mb-10">
        {t.leaderboard.pageIntro}
      </p>

      <div className="flex gap-2 mb-10">
        <button
          data-testid="leaderboard-scope-month"
          onClick={() => setScope("month")}
          className={`text-xs uppercase tracking-[0.22em] px-4 py-2 border ${scope === "month" ? "bg-[color:var(--navy)] text-white border-[color:var(--navy)]" : "border-[color:rgba(0,16,48,0.2)]"}`}
        >
          {t.leaderboard.month}
        </button>
        <button
          data-testid="leaderboard-scope-all"
          onClick={() => setScope("all")}
          className={`text-xs uppercase tracking-[0.22em] px-4 py-2 border ${scope === "all" ? "bg-[color:var(--navy)] text-white border-[color:var(--navy)]" : "border-[color:rgba(0,16,48,0.2)]"}`}
        >
          {t.leaderboard.allTime}
        </button>
      </div>

      {rows.length === 0 ? (
        <p data-testid="leaderboard-empty" className="text-[color:var(--ink-3)] italic font-serif-display text-2xl">{t.leaderboard.empty}</p>
      ) : (
        <ol className="space-y-3">
          {rows.map((r, i) => (
            <li
              key={r.user_id}
              data-testid={`leaderboard-row-${i + 1}`}
              className={`flex items-center gap-5 p-4 border ${i === 0 ? "border-[color:var(--red)] border-2 bg-white" : "border-[color:rgba(0,16,48,0.1)] bg-white"}`}
            >
              <span className="font-serif-display text-3xl w-12 text-center">{i + 1}</span>
              <div className="w-12 h-12 rounded-full bg-[var(--bg-2)] flex items-center justify-center font-serif-display text-base text-[color:var(--navy)]">
                {INITIALS(r.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif-display text-xl truncate">{r.name}</p>
                <p className="eyebrow mt-1">{t.tier[r.tier] || r.tier}</p>
              </div>
              <div className="text-right">
                <p className="font-serif-display text-3xl leading-none">{r.points}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--ink-3)] mt-1">pts</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
